import VoiceAPI from './voiceAPIClient';
import TwilioVoiceClient from './twilioVoiceClient';

const getPluginSystem = () => {
  return window.chatwoot?.PluginSystem || window.PluginSystem;
};

const getCallAdapter = inbox => {
  const pluginSystem = getPluginSystem();
  if (!pluginSystem?.executeHook || !inbox) {
    return null;
  }

  const result = pluginSystem.executeHook('voice:call-adapter', {
    inbox,
    adapter: null,
  });

  return result?.adapter || null;
};

const CallProviderClient = {
  hasAdapter(inbox) {
    return Boolean(getCallAdapter(inbox));
  },

  async startOutboundCall({ inbox, phoneNumber, contactId }) {
    const adapter = getCallAdapter(inbox);
    if (!adapter?.startOutboundCall) {
      throw new Error('Outbound browser call is not available for this inbox.');
    }

    return adapter.startOutboundCall({ inbox, phoneNumber, contactId });
  },

  async joinCall({ conversationId, inbox, callSid, callDirection }) {
    const adapter = getCallAdapter(inbox);
    if (adapter?.joinCall) {
      return adapter.joinCall({
        conversationId,
        inbox,
        callSid,
        callDirection,
      });
    }

    const device = await TwilioVoiceClient.initializeDevice(inbox.id);
    if (!device) return null;

    const joinResponse = await VoiceAPI.joinConference({
      conversationId,
      inboxId: inbox.id,
      callSid,
    });

    await TwilioVoiceClient.joinClientCall({
      to: joinResponse?.conference_sid,
      conversationId,
    });

    return { conferenceSid: joinResponse?.conference_sid };
  },

  async endCall({ conversationId, inbox, callSid }) {
    const adapter = getCallAdapter(inbox);
    if (adapter?.endCall) {
      return adapter.endCall({
        conversationId,
        inbox,
        callSid,
      });
    }

    await VoiceAPI.leaveConference(inbox.id, conversationId);
    TwilioVoiceClient.endClientCall();
    return null;
  },

  rejectIncomingCall({ inbox, callSid }) {
    const adapter = getCallAdapter(inbox);
    if (adapter?.rejectIncomingCall) {
      adapter.rejectIncomingCall({ inbox, callSid }).catch(error => {
        // eslint-disable-next-line no-console
        console.error(
          '[VOICE-CALL] Failed to reject incoming browser call:',
          error
        );
      });
      return;
    }

    TwilioVoiceClient.endClientCall();
  },

  subscribeToDisconnect(handler) {
    const twilioHandler = () => handler({ provider: 'twilio' });

    TwilioVoiceClient.addEventListener('call:disconnected', twilioHandler);

    return () => {
      TwilioVoiceClient.removeEventListener('call:disconnected', twilioHandler);
    };
  },
};

export default CallProviderClient;
